import React from 'react';
import type { FormsKpiStats } from '../../forms/types';
import { cn } from '../../lib/cn';
import { Icon } from '../Icon/Icon';
import type { IconKind } from '../Icon/Icon';

type KpiCard = {
  label: string;
  value: string;
  hint: string;
  hintTone?: string;
  icon: IconKind;
  accent: string;
};

function buildCards(stats: FormsKpiStats): KpiCard[] {
  return [
    {
      label: 'Sent this month',
      value: stats.sent.toLocaleString(),
      hint: '+18% vs. last month',
      hintTone: 'var(--success)',
      icon: 'send',
      accent: 'var(--ai-3)',
    },
    {
      label: 'Completed',
      value: stats.completed.toLocaleString(),
      hint: `${String(stats.completionRate)}% completion rate`,
      hintTone: 'var(--gold)',
      icon: 'check-circ',
      accent: 'var(--success)',
    },
    {
      label: 'Pending review',
      value: String(stats.pendingReview),
      hint: '2 require provider sign-off',
      hintTone: 'var(--terracotta)',
      icon: 'alert',
      accent: 'var(--terracotta)',
    },
  ];
}

export interface FormsKpiStripProps {
  stats: FormsKpiStats;
}

export function FormsKpiStrip({ stats }: FormsKpiStripProps) {
  const cards = buildCards(stats);

  return (
    <div className="fm-kpi-strip mb-[18px] grid shrink-0 grid-cols-3 border-y border-[var(--cream-2)] py-5">
      {cards.map((card, index) => (
        <div
          key={card.label}
          className={cn('flex min-w-0 items-start gap-3 px-6', index > 0 && 'border-l border-[var(--cream-2)]')}
        >
          <span
            className="inline-flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-[7px] border border-[var(--cream-2)] bg-[var(--cream-0)]"
            style={{ color: card.accent }}
          >
            <Icon kind={card.icon} size={14} stroke={1.8} />
          </span>
          <div className="min-w-0">
            <p className="mb-1.5 font-[var(--font-mono)] text-[10px] font-bold uppercase tracking-[0.13em] text-[var(--ink-3)]">
              {card.label}
            </p>
            <div className="flex flex-wrap items-baseline gap-2.5">
              <span className="font-[var(--font-display)] text-[28px] font-semibold leading-none tracking-[-0.02em] text-[var(--ink-0)]">
                {card.value}
              </span>
              <span className="text-[11.5px] font-medium" style={{ color: card.hintTone ?? 'var(--ink-3)' }}>
                {card.hint}
              </span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
